import React from "react";
import { structuredValue } from "../utils/tablePresentation";
import api, { postForm } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useOrg } from "../context/OrgContext";
import { useTerminology } from "../context/TerminologyContext";
import PageHeader from "../components/PageHeader";
import { downloadBlob } from "../utils/download";
import {
  DateRangePicker,
  ModalShell,
  Pager,
  usePagination,
} from "../components/ui-helpers";
import { formatDateTime } from "../utils/datetime";
import PinModal, { usePinGate } from "../components/PinModal";

const QUOTA_MODES = [
  { value: "monthly", label: "Monthly only" },
  { value: "yearly", label: "Yearly only" },
  { value: "both", label: "Monthly + Yearly" },
];

function displayActor(value) {
  return String(value || "")
    .trim()
    .toLowerCase() === "queue_worker"
    ? "System"
    : structuredValue(value);
}

function pctClass(pct, yellow, red) {
  const p = Number(pct) || 0;
  if (p >= Number(red || 90)) return "usage-pct usage-pct-red";
  if (p >= Number(yellow || 70)) return "usage-pct usage-pct-yellow";
  return "usage-pct usage-pct-normal";
}

function StatusCard({ status, lotPluralLabel = "Lots" }) {
  if (!status) return null;
  const mode = String(status.quota_mode || "both").toLowerCase();
  const showMonthly = mode === "monthly" || mode === "both";
  const showYearly = mode === "yearly" || mode === "both";
  const yellow = status.yellow_threshold_pct ?? 70;
  const red = status.red_threshold_pct ?? 90;
  return (
    <div className="section usage-status-card">
      <div className="usage-status-grid">
        {showMonthly ? (
          <div className="usage-status-tile">
            <div className="usage-status-label">Monthly</div>
            <div className="usage-status-value">
              <strong>{status.monthly?.in_use ?? 0}</strong> /{" "}
              {status.monthly_limit ?? 0}
              <span
                className={pctClass(status.monthly?.percent_used, yellow, red)}
              >
                {Number(status.monthly?.percent_used || 0).toFixed(1)}%
              </span>
            </div>
            <div className="usage-status-detail">
              Used {status.monthly?.used ?? 0} • Reserved{" "}
              {status.monthly?.reserved ?? 0} • Available{" "}
              {status.monthly?.available ?? 0}
            </div>
          </div>
        ) : null}
        {showYearly ? (
          <div className="usage-status-tile">
            <div className="usage-status-label">Yearly</div>
            <div className="usage-status-value">
              <strong>{status.yearly?.in_use ?? 0}</strong> /{" "}
              {status.yearly_limit ?? 0}
              <span
                className={pctClass(status.yearly?.percent_used, yellow, red)}
              >
                {Number(status.yearly?.percent_used || 0).toFixed(1)}%
              </span>
            </div>
            <div className="usage-status-detail">
              Used {status.yearly?.used ?? 0} • Reserved{" "}
              {status.yearly?.reserved ?? 0} • Available{" "}
              {status.yearly?.available ?? 0}
            </div>
          </div>
        ) : null}
        <div className="usage-status-tile">
          <div className="usage-status-label">On hold</div>
          <div className="usage-status-value">
            <strong>{status.held_count ?? 0}</strong>
          </div>
          <div className="usage-status-detail">
            {lotPluralLabel} waiting for credits in this workspace
          </div>
        </div>
        <div className="usage-status-tile">
          <div className="usage-status-label">Thresholds</div>
          <div className="usage-status-value">
            <span className="usage-pct usage-pct-yellow">
              Yellow ≥ {yellow}%
            </span>{" "}
            <span className="usage-pct usage-pct-red">Red ≥ {red}%</span>
          </div>
          <div className="usage-status-detail">
            Mode: {status.quota_mode || "both"}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UsageLedgerPage() {
  const { user } = useAuth();
  const pinGate = usePinGate();
  const { current: currentOrg } = useOrg();
  const { t, tp } = useTerminology();
  const role = user?.currentRole || "";
  const isSuperAdmin = role === "Super Admin";
  const isAdmin = role === "Admin";

  const [status, setStatus] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [sortBy, setSortBy] = React.useState("created_at");
  const [order, setOrder] = React.useState("desc");
  const [dateDraft, setDateDraft] = React.useState({
    date_from: "",
    date_to: "",
  });
  const [dateRange, setDateRange] = React.useState({
    date_from: "",
    date_to: "",
  });
  const [requests, setRequests] = React.useState([]);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");

  const [reqOpen, setReqOpen] = React.useState(false);
  const [reqForm, setReqForm] = React.useState({
    monthly: "",
    yearly: "",
    note: "",
  });

  const [cfgOpen, setCfgOpen] = React.useState(false);
  const [cfgForm, setCfgForm] = React.useState({});

  const [adjOpen, setAdjOpen] = React.useState(false);
  const [adjForm, setAdjForm] = React.useState({
    monthly_delta: "",
    yearly_delta: "",
    note: "",
  });

  const [decideTarget, setDecideTarget] = React.useState(null);
  const [decideForm, setDecideForm] = React.useState({
    monthly: "",
    yearly: "",
    note: "",
  });

  const flash = (m, e = "") => {
    setMessage(m);
    setError(e);
    setTimeout(() => {
      setMessage("");
      setError("");
    }, 4000);
  };

  const loadStatus = React.useCallback(async () => {
    try {
      const r = await api.get("/usage/status");
      setStatus(r.data?.status || null);
    } catch {
      setStatus(null);
    }
  }, []);

  const loadLedger = React.useCallback(async () => {
    try {
      const r = await api.get("/usage/ledger", {
        params: {
          q,
          sort_by: sortBy,
          order,
          page: 1,
          page_size: 500,
          date_from: dateRange.date_from || undefined,
          date_to: dateRange.date_to || undefined,
        },
      });
      setRows(r.data?.rows || []);
    } catch {
      setRows([]);
    }
  }, [q, sortBy, order, dateRange]);

  const loadRequests = React.useCallback(async () => {
    try {
      const r = await api.get("/usage/requests", {
        params: isSuperAdmin ? {} : { scope: "org" },
      });
      setRequests(r.data?.items || []);
    } catch {
      setRequests([]);
    }
  }, [isSuperAdmin]);

  React.useEffect(() => {
    loadStatus();
  }, [loadStatus, currentOrg]);
  React.useEffect(() => {
    loadLedger();
  }, [loadLedger, currentOrg]);
  React.useEffect(() => {
    if (isSuperAdmin || isAdmin) loadRequests();
  }, [loadRequests, currentOrg, isSuperAdmin, isAdmin]);

  const performSubmitRequest = async (payload) => {
    try {
      await postForm("/usage/request_credits", payload);
      setReqOpen(false);
      setReqForm({ monthly: "", yearly: "", note: "" });
      await loadRequests();
      flash("Credit request submitted.");
    } catch (e) {
      setError(e?.response?.data?.error || "Could not submit request.");
    }
  };

  const submitRequest = () => {
    setError("");
    setMessage("");
    const m = parseInt(reqForm.monthly || "0", 10) || 0;
    const y = parseInt(reqForm.yearly || "0", 10) || 0;
    if (m <= 0 && y <= 0) {
      setError("Enter monthly or yearly amount.");
      return;
    }
    const payload = {
      requested_by: user?.uid || "",
      requested_monthly: String(m),
      requested_yearly: String(y),
      note: reqForm.note || "",
    };
    pinGate.askPinThen(() => performSubmitRequest(payload));
  };

  const openConfig = () => {
    if (!status) return;
    const policy = status.ml_outage_policy || {};
    const validationPolicy = status.extraction_validation_policy || {};
    setCfgForm({
      quota_mode: status.quota_mode || "both",
      monthly_limit: status.monthly_limit ?? 1000,
      yearly_limit: status.yearly_limit ?? 12000,
      yellow_threshold_pct: status.yellow_threshold_pct ?? 70,
      red_threshold_pct: status.red_threshold_pct ?? 90,
      ml_outage_fallback_enabled: policy.enabled !== false,
      ml_outage_retry_cooldown_minutes: policy.retry_cooldown_minutes ?? 3,
      ml_outage_max_attempts: policy.max_attempts ?? 10,
      ml_outage_fallback_batch_size: policy.fallback_batch_size ?? 3,
      extraction_validation_retry_max: validationPolicy.retry_max ?? 3,
      extraction_validation_retry_backoff_seconds:
        validationPolicy.retry_backoff_seconds ?? 60,
      extraction_validation_partial_accept_enabled:
        validationPolicy.partial_accept_enabled !== false,
      extraction_validation_partial_max_rows:
        validationPolicy.partial_max_rows ?? 2,
    });
    setCfgOpen(true);
  };

  const saveConfig = async () => {
    const cooldown = parseInt(cfgForm.ml_outage_retry_cooldown_minutes, 10);
    const attempts = parseInt(cfgForm.ml_outage_max_attempts, 10);
    const batch = parseInt(cfgForm.ml_outage_fallback_batch_size, 10);
    const validationRetries = parseInt(
      cfgForm.extraction_validation_retry_max,
      10,
    );
    const validationBackoff = parseInt(
      cfgForm.extraction_validation_retry_backoff_seconds,
      10,
    );
    const partialMaxRows = parseInt(
      cfgForm.extraction_validation_partial_max_rows,
      10,
    );
    if (!Number.isFinite(cooldown) || cooldown <= 0 || cooldown > 1440) {
      setError("ML outage retry cooldown must be between 1 and 1440 minutes.");
      return;
    }
    if (!Number.isFinite(attempts) || attempts <= 0 || attempts > 100) {
      setError("ML outage max attempts must be between 1 and 100.");
      return;
    }
    if (!Number.isFinite(batch) || batch <= 0 || batch > 100) {
      setError("ML outage LLM fallback batch size must be between 1 and 100.");
      return;
    }
    if (
      !Number.isFinite(validationRetries) ||
      validationRetries < 0 ||
      validationRetries > 1000
    ) {
      setError("Validation retries must be between 0 and 1000.");
      return;
    }
    if (
      !Number.isFinite(validationBackoff) ||
      validationBackoff < 1 ||
      validationBackoff > 86400
    ) {
      setError("Validation retry backoff must be between 1 and 86400 seconds.");
      return;
    }
    if (
      !Number.isFinite(partialMaxRows) ||
      partialMaxRows < 0 ||
      partialMaxRows > 1000
    ) {
      setError("Maximum isolated mapping gaps must be between 0 and 1000.");
      return;
    }
    try {
      await postForm("/usage/config", {
        quota_mode: cfgForm.quota_mode,
        monthly_limit: String(cfgForm.monthly_limit),
        yearly_limit: String(cfgForm.yearly_limit),
        yellow_threshold_pct: String(cfgForm.yellow_threshold_pct),
        red_threshold_pct: String(cfgForm.red_threshold_pct),
        ml_outage_fallback_enabled: cfgForm.ml_outage_fallback_enabled
          ? "1"
          : "0",
        ml_outage_retry_cooldown_minutes: String(cooldown),
        ml_outage_max_attempts: String(attempts),
        ml_outage_fallback_batch_size: String(batch),
        extraction_validation_retry_max: String(validationRetries),
        extraction_validation_retry_backoff_seconds: String(validationBackoff),
        extraction_validation_partial_accept_enabled:
          cfgForm.extraction_validation_partial_accept_enabled ? "1" : "0",
        extraction_validation_partial_max_rows: String(partialMaxRows),
        actor: user?.uid || "",
      });
      setCfgOpen(false);
      await loadStatus();
      await loadLedger();
      flash("Quota, ML outage, and extraction validation policy saved.");
    } catch (e) {
      setError(e?.response?.data?.error || "Could not save config.");
    }
  };

  const submitAdjust = async () => {
    try {
      await postForm("/usage/adjust", {
        monthly_delta: String(parseInt(adjForm.monthly_delta || "0", 10) || 0),
        yearly_delta: String(parseInt(adjForm.yearly_delta || "0", 10) || 0),
        note: adjForm.note || "",
        actor: user?.uid || "",
      });
      setAdjOpen(false);
      setAdjForm({ monthly_delta: "", yearly_delta: "", note: "" });
      await loadStatus();
      await loadLedger();
      flash("Quota adjusted.");
    } catch (e) {
      setError(e?.response?.data?.error || "Could not adjust.");
    }
  };

  const decide = async (action) => {
    if (!decideTarget) return;
    try {
      await postForm("/usage/requests/decide", {
        request_id: String(decideTarget.id),
        action,
        decided_by: user?.uid || "",
        decided_monthly:
          decideForm.monthly !== ""
            ? String(parseInt(decideForm.monthly, 10) || 0)
            : "",
        decided_yearly:
          decideForm.yearly !== ""
            ? String(parseInt(decideForm.yearly, 10) || 0)
            : "",
        decision_note: decideForm.note || "",
      });
      setDecideTarget(null);
      setDecideForm({ monthly: "", yearly: "", note: "" });
      await loadRequests();
      await loadStatus();
      await loadLedger();
      flash(`Request ${action}d.`);
    } catch (e) {
      setError(e?.response?.data?.error || "Could not decide request.");
    }
  };

  const downloadLedgerExport = async (extension) => {
    try {
      const res = await api.get(`/export/usage-ledger.${extension}`, {
        params: {
          q,
          sort_by: sortBy,
          order,
          date_from: dateRange.date_from || undefined,
          date_to: dateRange.date_to || undefined,
        },
        responseType: "blob",
      });
      downloadBlob(`usage-ledger.${extension}`, res.data);
    } catch {
      flash("", `Could not download usage ledger ${extension === "xlsx" ? "Excel" : "CSV"}.`);
    }
  };

  const ledgerPager = usePagination(rows, 25);

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Operations"
        icon="usage"
        title="Usage Ledger"
        subtitle={
          isSuperAdmin
            ? "Track organization credit usage, configure limits, and review every quota event."
            : "Track workspace credit usage, request more credits, and review every quota event."
        }
      />

      {message ? <div className="banner success">{message}</div> : null}
      {error ? <div className="banner error">{error}</div> : null}

      <StatusCard status={status} lotPluralLabel={tp("lot")} />

      <div className="section usage-actions-row">
        {isAdmin ? (
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => setReqOpen(true)}
          >
            Request more credits
          </button>
        ) : null}
        {isSuperAdmin ? (
          <>
            <button
              className="btn btn-primary"
              type="button"
              onClick={openConfig}
            >
              Configure quota
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setAdjOpen(true)}
            >
              Adjust credits
            </button>
          </>
        ) : null}
      </div>

      {isSuperAdmin || isAdmin ? (
        <div className="section">
          <div className="page-header-row">
            <div>
              <h3>Credit requests</h3>
              <div className="helper-text">
                {isSuperAdmin
                  ? "Approve, reject, or grant a custom amount."
                  : "Track the status of credit requests submitted for this workspace."}
              </div>
            </div>
          </div>
          <div className="table--card">
            <div className="table-scroll">
              <table className="table table--dense table--fixed">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Requested by</th>
                    <th>Monthly</th>
                    <th>Yearly</th>
                    <th>Note</th>
                    <th>Status</th>
                    <th>Decided</th>
                    {isSuperAdmin ? <th>Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.id}>
                      <td>{formatDateTime(r.created_at)}</td>
                      <td>{r.requested_by}</td>
                      <td>{r.requested_monthly || 0}</td>
                      <td>{r.requested_yearly || 0}</td>
                      <td>{r.note || "—"}</td>
                      <td>
                        <span
                          className={`status-pill ${r.status === "approved" ? "status-approved" : r.status === "rejected" ? "status-rejected" : "status-pending"}`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td>
                        {r.decided_at
                          ? `${formatDateTime(r.decided_at)} by ${r.decided_by || "—"}`
                          : "—"}
                      </td>
                      {isSuperAdmin ? (
                        <td>
                          {r.status === "pending" ? (
                            <button
                              className="btn btn-secondary"
                              type="button"
                              onClick={() => {
                                setDecideTarget(r);
                                setDecideForm({
                                  monthly: String(r.requested_monthly || 0),
                                  yearly: String(r.requested_yearly || 0),
                                  note: "",
                                });
                              }}
                            >
                              Review
                            </button>
                          ) : (
                            "—"
                          )}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                  {requests.length === 0 ? (
                    <tr>
                      <td colSpan={isSuperAdmin ? 8 : 7}>
                        No credit requests yet.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      <div className="section">
        <div className="filters-bar">
          <label className="field-grow">
            <span>Search</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${t("vendor").toLowerCase()}, SKU, ${t("lot").toLowerCase()}, event, or actor`}
            />
          </label>
          <label>
            <span>Sort by</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="created_at">Created</option>
              <option value="event_type">Event</option>
              <option value="units">Units</option>
              <option value="vendor">{t("vendor")}</option>
              <option value="sku">SKU</option>
              <option value="lot_no">{t("lot")}</option>
            </select>
          </label>
          <label>
            <span>Order</span>
            <select value={order} onChange={(e) => setOrder(e.target.value)}>
              <option value="desc">Desc</option>
              <option value="asc">Asc</option>
            </select>
          </label>
          <DateRangePicker
            value={dateDraft}
            onChange={setDateDraft}
            onApply={() => setDateRange({ ...dateDraft })}
            onClear={(empty) => setDateRange(empty)}
          />
          <div className="status-toolbar-actions">
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => downloadLedgerExport("csv")}
            >
              Download CSV
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => downloadLedgerExport("xlsx")}
            >
              Download Excel
            </button>
          </div>
        </div>
        <div className="table--card">
          <div className="table-scroll">
            <table className="table table--dense table--fixed">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Event</th>
                  <th>Units</th>
                  <th>{t("vendor")}</th>
                  <th>SKU</th>
                  <th>{t("lot")}</th>
                  <th>By</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {ledgerPager.pageData.map((row) => (
                  <tr key={row.id}>
                    <td>{structuredValue(formatDateTime(row.created_at))}</td>
                    <td>
                      <span className="status-pill status-pending">
                        {structuredValue(row.event_type)}
                      </span>
                    </td>
                    <td>{structuredValue(row.units)}</td>
                    <td>{structuredValue(row.vendor)}</td>
                    <td>{structuredValue(row.sku)}</td>
                    <td>{structuredValue(row.lot_no)}</td>
                    <td>{displayActor(row.created_by)}</td>
                    <td>{row.note || "—"}</td>
                  </tr>
                ))}
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8}>No ledger events yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <Pager {...ledgerPager} />
        </div>
      </div>

      <ModalShell
        open={reqOpen}
        onClose={() => setReqOpen(false)}
        title="Request more credits"
        subtitle="Send a request for additional monthly or yearly credits."
        size="md"
        actions={
          <>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setReqOpen(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={submitRequest}
            >
              Submit request
            </button>
          </>
        }
      >
        <div className="form-grid">
          <div className="field span-6">
            <label>
              Monthly credits
              <input
                type="number"
                min="0"
                value={reqForm.monthly}
                onChange={(e) =>
                  setReqForm((c) => ({ ...c, monthly: e.target.value }))
                }
              />
            </label>
          </div>
          <div className="field span-6">
            <label>
              Yearly credits
              <input
                type="number"
                min="0"
                value={reqForm.yearly}
                onChange={(e) =>
                  setReqForm((c) => ({ ...c, yearly: e.target.value }))
                }
              />
            </label>
          </div>
          <div className="field span-12">
            <label>
              Note
              <textarea
                rows={3}
                value={reqForm.note}
                onChange={(e) =>
                  setReqForm((c) => ({ ...c, note: e.target.value }))
                }
              />
            </label>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        open={cfgOpen}
        onClose={() => setCfgOpen(false)}
        title="Configure quota, ML outage, and validation policy"
        subtitle="Set workspace quota limits, ML outage behavior, and extraction validation recovery."
        size="lg"
        actions={
          <>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setCfgOpen(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={saveConfig}
            >
              Save
            </button>
          </>
        }
      >
        <div className="form-grid">
          <div className="field span-12">
            <div className="field-section-heading">Quota</div>
          </div>
          <div className="field span-6">
            <label>
              Quota mode
              <select
                value={cfgForm.quota_mode || "both"}
                onChange={(e) =>
                  setCfgForm((c) => ({ ...c, quota_mode: e.target.value }))
                }
              >
                {QUOTA_MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="field span-3">
            <label>
              Monthly limit
              <input
                type="number"
                min="0"
                value={cfgForm.monthly_limit ?? 0}
                onChange={(e) =>
                  setCfgForm((c) => ({ ...c, monthly_limit: e.target.value }))
                }
              />
            </label>
          </div>
          <div className="field span-3">
            <label>
              Yearly limit
              <input
                type="number"
                min="0"
                value={cfgForm.yearly_limit ?? 0}
                onChange={(e) =>
                  setCfgForm((c) => ({ ...c, yearly_limit: e.target.value }))
                }
              />
            </label>
          </div>
          <div className="field span-6">
            <label>
              Yellow threshold (%)
              <input
                type="number"
                min="0"
                max="100"
                value={cfgForm.yellow_threshold_pct ?? 70}
                onChange={(e) =>
                  setCfgForm((c) => ({
                    ...c,
                    yellow_threshold_pct: e.target.value,
                  }))
                }
              />
            </label>
          </div>
          <div className="field span-6">
            <label>
              Red threshold (%)
              <input
                type="number"
                min="0"
                max="100"
                value={cfgForm.red_threshold_pct ?? 90}
                onChange={(e) =>
                  setCfgForm((c) => ({
                    ...c,
                    red_threshold_pct: e.target.value,
                  }))
                }
              />
            </label>
          </div>
          <div className="field span-12">
            <div className="field-section-heading">ML outage policy</div>
          </div>
          <div className="field span-12">
            <label className="checkbox-line">
              <input
                type="checkbox"
                checked={cfgForm.ml_outage_fallback_enabled !== false}
                onChange={(e) =>
                  setCfgForm((c) => ({
                    ...c,
                    ml_outage_fallback_enabled: e.target.checked,
                  }))
                }
              />
              Enable ML outage LLM fallback
            </label>
            <div className="helper-text">
              When an ML workspace API is unreachable, retry first, then
              temporarily use LLM for the configured batch size. The requested
              engine stays ML.
            </div>
          </div>
          <div className="field span-4">
            <label>
              Retry cooldown (minutes)
              <input
                type="number"
                min="1"
                max="1440"
                value={cfgForm.ml_outage_retry_cooldown_minutes ?? 3}
                onChange={(e) =>
                  setCfgForm((c) => ({
                    ...c,
                    ml_outage_retry_cooldown_minutes: e.target.value,
                  }))
                }
              />
            </label>
          </div>
          <div className="field span-4">
            <label>
              Max health attempts
              <input
                type="number"
                min="1"
                max="100"
                value={cfgForm.ml_outage_max_attempts ?? 10}
                onChange={(e) =>
                  setCfgForm((c) => ({
                    ...c,
                    ml_outage_max_attempts: e.target.value,
                  }))
                }
              />
            </label>
          </div>
          <div className="field span-4">
            <label>
              Fallback batch size
              <input
                type="number"
                min="1"
                max="100"
                value={cfgForm.ml_outage_fallback_batch_size ?? 3}
                onChange={(e) =>
                  setCfgForm((c) => ({
                    ...c,
                    ml_outage_fallback_batch_size: e.target.value,
                  }))
                }
              />
            </label>
          </div>
          <div className="field span-12">
            <div className="field-section-heading">
              Extraction validation &amp; recovery
            </div>
            <div className="helper-text">
              Structural validation retries use the same queued extraction and
              do not reserve another credit.
            </div>
          </div>
          <div className="field span-6">
            <label>
              Validation retries
              <input
                type="number"
                min="0"
                max="1000"
                value={cfgForm.extraction_validation_retry_max ?? 3}
                onChange={(e) =>
                  setCfgForm((c) => ({
                    ...c,
                    extraction_validation_retry_max: e.target.value,
                  }))
                }
              />
            </label>
          </div>
          <div className="field span-6">
            <label>
              Retry backoff (seconds)
              <input
                type="number"
                min="1"
                max="86400"
                value={
                  cfgForm.extraction_validation_retry_backoff_seconds ?? 60
                }
                onChange={(e) =>
                  setCfgForm((c) => ({
                    ...c,
                    extraction_validation_retry_backoff_seconds:
                      e.target.value,
                  }))
                }
              />
            </label>
          </div>
          <div className="field span-12">
            <label className="checkbox-line">
              <input
                type="checkbox"
                checked={
                  cfgForm.extraction_validation_partial_accept_enabled !== false
                }
                onChange={(e) =>
                  setCfgForm((c) => ({
                    ...c,
                    extraction_validation_partial_accept_enabled:
                      e.target.checked,
                  }))
                }
              />
              Allow reviewable partial extraction
            </label>
            <div className="helper-text">
              After finite retries are exhausted, discard only isolated,
              deterministically identified mapping gaps and keep the remaining
              valid values.
            </div>
          </div>
          <div className="field span-6">
            <label>
              Maximum isolated mapping gaps
              <input
                type="number"
                min="0"
                max="1000"
                value={cfgForm.extraction_validation_partial_max_rows ?? 2}
                onChange={(e) =>
                  setCfgForm((c) => ({
                    ...c,
                    extraction_validation_partial_max_rows: e.target.value,
                  }))
                }
              />
            </label>
          </div>
          <div className="field span-12 helper-text">
            0 retries means unlimited validation retries. Backoff begins after
            an extraction attempt completes. A maximum gap value of 0 disables
            partial acceptance even when the checkbox is enabled.
          </div>
        </div>
      </ModalShell>

      <ModalShell
        open={adjOpen}
        onClose={() => setAdjOpen(false)}
        title="Adjust credits"
        subtitle="Manually add or subtract credits for this workspace. Positive adds headroom, negative reduces it."
        size="md"
        actions={
          <>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setAdjOpen(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={submitAdjust}
            >
              Apply
            </button>
          </>
        }
      >
        <div className="form-grid">
          <div className="field span-6">
            <label>
              Monthly delta
              <input
                type="number"
                value={adjForm.monthly_delta}
                onChange={(e) =>
                  setAdjForm((c) => ({ ...c, monthly_delta: e.target.value }))
                }
              />
            </label>
          </div>
          <div className="field span-6">
            <label>
              Yearly delta
              <input
                type="number"
                value={adjForm.yearly_delta}
                onChange={(e) =>
                  setAdjForm((c) => ({ ...c, yearly_delta: e.target.value }))
                }
              />
            </label>
          </div>
          <div className="field span-12">
            <label>
              Note
              <textarea
                rows={3}
                value={adjForm.note}
                onChange={(e) =>
                  setAdjForm((c) => ({ ...c, note: e.target.value }))
                }
              />
            </label>
          </div>
        </div>
      </ModalShell>

      <ModalShell
        open={!!decideTarget}
        onClose={() => setDecideTarget(null)}
        title="Decide credit request"
        subtitle="Approve with the requested amounts, override, or reject."
        size="md"
        actions={
          <>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setDecideTarget(null)}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              type="button"
              onClick={() => decide("reject")}
            >
              Reject
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => decide("approve")}
            >
              Approve
            </button>
          </>
        }
      >
        {decideTarget ? (
          <div className="form-grid">
            <div className="field span-12 helper-text">
              Organization: <strong>{decideTarget.org_id}</strong> • Requested
              by <strong>{decideTarget.requested_by}</strong>
            </div>
            <div className="field span-6">
              <label>
                Grant monthly
                <input
                  type="number"
                  min="0"
                  value={decideForm.monthly}
                  onChange={(e) =>
                    setDecideForm((c) => ({ ...c, monthly: e.target.value }))
                  }
                />
              </label>
            </div>
            <div className="field span-6">
              <label>
                Grant yearly
                <input
                  type="number"
                  min="0"
                  value={decideForm.yearly}
                  onChange={(e) =>
                    setDecideForm((c) => ({ ...c, yearly: e.target.value }))
                  }
                />
              </label>
            </div>
            <div className="field span-12">
              <label>
                Decision note
                <textarea
                  rows={3}
                  value={decideForm.note}
                  onChange={(e) =>
                    setDecideForm((c) => ({ ...c, note: e.target.value }))
                  }
                />
              </label>
            </div>
          </div>
        ) : null}
      </ModalShell>
      <PinModal
        uid={user?.uid || ""}
        open={pinGate.pinOpen}
        onClose={pinGate.closePin}
        onVerified={pinGate.handlePinVerified}
      />
    </div>
  );
}
